/**
 * PauseMenu
 * Full-screen tabbed pause overlay inspired by Pixel Gun 3D's in-match menu.
 * Tabs: Scoreboard (list of players), Tasks, Menu (resume/settings/quit).
 */

export interface PauseScoreboardRow {
  readonly rank: number;
  readonly name: string;
  readonly score: number;
  readonly kills: number;
  readonly isSelf?: boolean;
}

export interface PauseTask {
  readonly id: string;
  readonly text: string;
  readonly progress: number; // current
  readonly goal: number; // target
  readonly reward: number; // pixel bucks
  readonly done?: boolean;
}

export interface PauseMenuState {
  readonly secrets: number;
  readonly secretsTotal: number;
  readonly players: number;
  readonly kills: number;
  readonly bestPlayer: string | null;
  readonly squad: string | null;
  readonly scoreboard: readonly PauseScoreboardRow[];
  readonly tasks: readonly PauseTask[];
}

const EMPTY_STATE: PauseMenuState = {
  secrets: 0,
  secretsTotal: 0,
  players: 0,
  kills: 0,
  bestPlayer: null,
  squad: null,
  scoreboard: [],
  tasks: [],
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export class PauseMenu {
  private readonly root: HTMLElement;
  private readonly tabs: HTMLElement[] = [];
  private readonly panes: HTMLElement[] = [];
  private state: PauseMenuState = EMPTY_STATE;
  private open = false;

  onResume: (() => void) | null = null;
  onQuitToMenu: (() => void) | null = null;
  onSettings: (() => void) | null = null;
  onRefreshTasks: (() => void) | null = null;

  constructor(rootSelector: string) {
    const root = document.querySelector<HTMLElement>(rootSelector);
    if (root === null) {
      throw new Error(`Pause menu root not found: ${rootSelector}`);
    }
    this.root = root;

    this.tabs = Array.from(root.querySelectorAll<HTMLElement>('.pause-tab'));
    this.panes = Array.from(root.querySelectorAll<HTMLElement>('.pause-pane'));

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;
        if (target === undefined) return;
        this.activateTab(target);
      });
    }

    const resumeBtn = root.querySelector<HTMLButtonElement>('.pause-btn-resume');
    resumeBtn?.addEventListener('click', () => this.onResume?.());

    const settingsBtn = root.querySelector<HTMLButtonElement>('.pause-btn-settings');
    settingsBtn?.addEventListener('click', () => this.onSettings?.());

    const quitBtn = root.querySelector<HTMLButtonElement>('.pause-btn-quit');
    quitBtn?.addEventListener('click', () => this.onQuitToMenu?.());

    const refreshBtn = root.querySelector<HTMLButtonElement>('#pause-tasks-refresh');
    refreshBtn?.addEventListener('click', () => this.onRefreshTasks?.());

    this.root.addEventListener('mousedown', (event) => {
      event.stopPropagation();
    });
  }

  get isOpen(): boolean {
    return this.open;
  }

  show(): void {
    this.open = true;
    this.render();
    this.root.classList.add('open');
    this.root.setAttribute('aria-hidden', 'false');
  }

  hide(): void {
    this.open = false;
    this.root.classList.remove('open');
    this.root.setAttribute('aria-hidden', 'true');
  }

  toggle(): void {
    if (this.open) {
      this.hide();
    } else {
      this.show();
    }
  }

  update(state: Partial<PauseMenuState>): void {
    this.state = { ...this.state, ...state };
    if (this.open) this.render();
  }

  private activateTab(target: string): void {
    for (const tab of this.tabs) {
      const active = tab.dataset.tab === target;
      tab.classList.toggle('active', active);
    }
    for (const pane of this.panes) {
      const active = pane.dataset.pane === target;
      pane.classList.toggle('active', active);
    }
  }

  private render(): void {
    const s = this.state;
    const setText = (id: string, value: string): void => {
      const el = document.getElementById(id);
      if (el !== null) el.textContent = value;
    };
    setText('pause-secrets', `${String(s.secrets)}/${String(s.secretsTotal)}`);
    setText('pause-players', String(s.players));
    setText('pause-kills', String(s.kills));
    setText('pause-best', s.bestPlayer ?? '—');
    setText('pause-squad', s.squad ?? '—');

    this.renderScoreboard(s.scoreboard);
    this.renderTasks(s.tasks);
  }

  private renderScoreboard(rows: readonly PauseScoreboardRow[]): void {
    const body = document.getElementById('pause-scoreboard-body');
    if (body === null) return;
    if (rows.length === 0) {
      body.innerHTML = '<tr><td colspan="4" style="text-align:center;color:rgba(255,255,255,0.5);padding:18px;">No players</td></tr>';
      return;
    }
    const html = rows.map((row) => {
      const classes = ['pause-row'];
      if (row.rank <= 3) classes.push('top3');
      if (row.isSelf) classes.push('self');
      return `<tr class="${classes.join(' ')}">
        <td>${String(row.rank)}</td>
        <td>${escapeHtml(row.name)}</td>
        <td>${String(row.score)}</td>
        <td>${String(row.kills)}</td>
      </tr>`;
    }).join('');
    body.innerHTML = html;
  }

  private renderTasks(tasks: readonly PauseTask[]): void {
    const list = document.getElementById('pause-tasks-list');
    if (list === null) return;
    if (tasks.length === 0) {
      list.innerHTML = '<div style="color:rgba(255,255,255,0.5);padding:8px 4px;">No tasks available</div>';
      return;
    }
    const html = tasks.map((task) => {
      const done = task.done === true || task.progress >= task.goal;
      const cls = done ? 'pause-task done' : 'pause-task';
      return `<div class="${cls}">
        <div>
          <div class="pause-task-text">${escapeHtml(task.text)}</div>
          <div class="pause-task-progress">${String(task.progress)}/${String(task.goal)}</div>
        </div>
        <div class="pause-task-reward">+${String(task.reward)} Bucks</div>
      </div>`;
    }).join('');
    list.innerHTML = html;
  }
}
